import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Star,
    Users,
    DollarSign,
    MessageSquare,
    Filter,
    Search,
    MoreVertical,
    Bot,
    Send,
    CheckCircle,
    ThumbsUp,
    Edit3,
    Save,
    X,
    Trophy,
    Award,
    TrendingUp,
    LogOut,
    Key,
    ChevronDown,
    ChevronUp,
    ChevronLeft,
    ChevronRight,
    User,
    Shield,
    Settings,
    Building2,
    UserCircle,
    AlertCircle,
    Volume2,
    VolumeX
} from 'lucide-react';
import { useAuth } from '../../context/UnifiedAuthContext.jsx';
import { useToastContext } from '../../contexts/ToastContext.jsx';
import TechnicianSelectModal from '../../components/TechnicianSelectModal/TechnicianSelectModal.jsx';
import './Dashboard.scss';
import {dashboardAPI, reviewsAPI, techniciansAPI, googleAPI, adminAPI} from "../../utils/api.js";
import { anonymizeName, anonymizeCompanyName, anonymizeAddressString, censorAIResponse, censorReviewText } from '../../utils/demoMode.js';
import {
    playNotificationSound,
    isNotificationSoundEnabled,
    setNotificationSoundEnabled,
    initializeNotifications
} from '../../utils/notificationSound.js';

const Dashboard = () => {
    const { technician, logout, userRole, urlParams, isAdmin, isManager } = useAuth();
    
    // Memoize role checks to prevent unnecessary re-renders
    const isUserAdmin = useMemo(() => isAdmin(), [isAdmin]);
    const isUserManager = useMemo(() => isManager(), [isManager]);
    const canManageAssignments = useMemo(() => isUserAdmin || isUserManager, [isUserAdmin, isUserManager]);
    
    // Memoize filter options to prevent re-renders
    const starRatingOptions = useMemo(() => [
        { value: "", label: "All Ratings" },
        { value: "5", label: "⭐⭐⭐⭐⭐ (5 stars)" },
        { value: "4", label: "⭐⭐⭐⭐ (4 stars)" },
        { value: "3", label: "⭐⭐⭐ (3 stars)" },
        { value: "2", label: "⭐⭐ (2 stars)" },
        { value: "1", label: "⭐ (1 star)" }
    ], []);
    
    const assignmentOptions = useMemo(() => [
        { value: "", label: "All Reviews" },
        { value: "assigned", label: "Assigned" },
        { value: "unassigned", label: "Unassigned" }
    ], []);
    
    const responseOptions = useMemo(() => [
        { value: "", label: "All Reviews" },
        { value: "answered", label: "Answered" },
        { value: "unanswered", label: "Unanswered" }
    ], []);
    const { showSuccess, showError, showWarning, showInfo } = useToastContext();
    const navigate = useNavigate();
    const location = useLocation();
    const [reviews, setReviews] = useState([]);
    const [googleReviews, setGoogleReviews] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [starRatingFilter, setStarRatingFilter] = useState('');
    const [assignmentFilter, setAssignmentFilter] = useState('');
    const [responseFilter, setResponseFilter] = useState('');
    const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
    const [loadingResponses, setLoadingResponses] = useState({});
    const [loadingResponseApprovals, setLoadingResponseApprovals] = useState({});
    // Removed collapsed responses - always keep AI responses open for conversation view
    const [openMenuId, setOpenMenuId] = useState(null);
    const [editingPersona, setEditingPersona] = useState(null);
    const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
    const [personaForm, setPersonaForm] = useState({
        traits: ['', '', ''],
        personality: ['', ''],
        communicationStyle: ['', '']
    });
    const [savingPersona, setSavingPersona] = useState(false);
    const [stats, setStats] = useState({
        totalReviews: 0,
        averageRating: 0,
        activeTechnicians: 0,
        totalRewards: 0
    });
    const [topTechnicians, setTopTechnicians] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filteringLoading, setFilteringLoading] = useState(false);
    const [paginationLoading, setPaginationLoading] = useState(false);
    const [error, setError] = useState('');
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [reviewToAssign, setReviewToAssign] = useState(null);
    const [showReassignConfirmModal, setShowReassignConfirmModal] = useState(false);
    const [reviewToReassign, setReviewToReassign] = useState(null);
    const [showRewardsDropdown, setShowRewardsDropdown] = useState(false);
    const [showRewardsModal, setShowRewardsModal] = useState(false);
    const [maxRewardsLimit, setMaxRewardsLimit] = useState(10);
    const [tempMaxRewardsLimit, setTempMaxRewardsLimit] = useState(10);
    const [maxRewardAmount, setMaxRewardAmount] = useState(1000);
    const [tempMaxRewardAmount, setTempMaxRewardAmount] = useState(1000);
    const [minimumPayoutThreshold, setMinimumPayoutThreshold] = useState(50);
    const [tempMinimumPayoutThreshold, setTempMinimumPayoutThreshold] = useState(50);
    const [rewardAmountPerReview, setRewardAmountPerReview] = useState(5);
    const [tempRewardAmountPerReview, setTempRewardAmountPerReview] = useState(5);
    const [settingsLoaded, setSettingsLoaded] = useState(false);
    
    // Pagination and filtering state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalReviews, setTotalReviews] = useState(0);
    const [reviewsPerPage, setReviewsPerPage] = useState(10); // Start with default, will be updated after auth loads
    const [sortBy, setSortBy] = useState('create_time');
    const [sortOrder, setSortOrder] = useState('DESC');

    const menuRef = useRef(null);
    const profileDropdownRef = useRef(null);
    const rewardsDropdownRef = useRef(null);

    // Sound notification state
    const [soundEnabled, setSoundEnabled] = useState(isNotificationSoundEnabled());
    const previousReviewIds = useRef(new Set());
    const isInitialLoad = useRef(true);

    // Helper function to build URLs with preserved parameters
    const buildUrlWithParams = (path) => {
        const searchParams = new URLSearchParams();

        // Preserve Ejento parameters
        if (urlParams.location) searchParams.set('location', urlParams.location);
        if (urlParams.user) searchParams.set('user', urlParams.user);
        // Preserve integration parameter for GoHighLevel users
        if (urlParams.integration) searchParams.set('integration', urlParams.integration);

        const queryString = searchParams.toString();
        return queryString ? `${path}?${queryString}` : path;
    };

    // Set optimal page size once auth is loaded
    useEffect(() => {
        if (technician) {
            const optimalPageSize = isUserAdmin ? 25 : 10;
            if (reviewsPerPage !== optimalPageSize) {
                setReviewsPerPage(optimalPageSize);
            }
        }
    }, [technician, isUserAdmin, reviewsPerPage]);

    useEffect(() => {
        fetchDashboardData();

        // Poll for new reviews every 30 seconds
        const pollInterval = setInterval(() => {
            fetchReviewsForPolling();
        }, 30000);

        // Refresh when window regains focus
        const handleFocus = () => {
            fetchReviewsForPolling();
        };
        window.addEventListener('focus', handleFocus);

        // Cleanup
        return () => {
            clearInterval(pollInterval);
            window.removeEventListener('focus', handleFocus);
        };
    }, []);

    // Fetch reviews for polling (silent, no loading state)
    const fetchReviewsForPolling = async () => {
        try {
            const reviewParams = {
                page: 1,
                limit: reviewsPerPage,
                sortBy: 'create_time',
                sortOrder: 'DESC'
            };

            const reviewsResult = await reviewsAPI.getReviews(reviewParams);

            if (reviewsResult.data?.reviews) {
                const newReviews = reviewsResult.data.reviews;

                // Check for new reviews (only after initial load)
                if (!isInitialLoad.current && newReviews.length > 0) {
                    const newReviewItems = newReviews.filter(
                        r => !previousReviewIds.current.has(r.id)
                    );

                    if (newReviewItems.length > 0) {
                        console.log(`🔔 ${newReviewItems.length} new review(s) detected!`);
                        playNotificationSound();
                        // Refresh the full dashboard to show new reviews
                        fetchDashboardData();
                    }
                }

                // Update previous IDs for next comparison
                previousReviewIds.current = new Set(newReviews.map(r => r.id));
                isInitialLoad.current = false;
            }
        } catch (error) {
            // Silent fail for polling
            console.warn('Polling error:', error);
        }
    };

    // Listen for refresh parameter to refresh data after admin operations
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const refreshParam = searchParams.get('refresh');
        
        if (refreshParam) {
            // Remove the refresh parameter from URL without causing a re-render loop
            searchParams.delete('refresh');
            const newSearch = searchParams.toString();
            const newUrl = location.pathname + (newSearch ? '?' + newSearch : '');
            window.history.replaceState({}, '', newUrl);
            
            // Refresh dashboard data
            fetchDashboardData();
        }
    }, [location.search]);

    // Close menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setOpenMenuId(null);
                setEditingPersona(null);
            }

            if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
                setProfileDropdownOpen(false);
            }

            if (rewardsDropdownRef.current && !rewardsDropdownRef.current.contains(event.target)) {
                setShowRewardsDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const fetchDashboardData = async (page = currentPage) => {
        try {
            setLoading(true);
            setError('');

            // Load reviews first for better perceived performance
            const reviewsPromise = fetchReviews(page);
            
            // Then load secondary data in parallel
            const [statsResult, topTechResult, rewardSettingsResult, leaderboardSettingsResult] = await Promise.all([
                dashboardAPI.getStats(),
                dashboardAPI.getTopTechnicians(),
                dashboardAPI.getRewardSettings().catch(error => {
                    return { success: false, error: error.message };
                }),
                dashboardAPI.getLeaderboardSettings().catch(error => {
                    return { success: false, error: error.message };
                })
            ]);

            setStats(statsResult.data);
            setTopTechnicians(topTechResult.data);

            // Load reward settings (payment-related)
            if (rewardSettingsResult.success && rewardSettingsResult.data) {
                const settings = rewardSettingsResult.data.rewardSettings || {};
                setMaxRewardAmount(settings.maxRewardAmount || 1000);
                setMinimumPayoutThreshold(settings.minimumPayoutThreshold || 50);
                setRewardAmountPerReview(settings.rewardAmountPerReview || 5);
            } else {
                setMaxRewardAmount(1000);
                setMinimumPayoutThreshold(50);
                setRewardAmountPerReview(5);
            }

            // Load leaderboard settings (display-related)
            if (leaderboardSettingsResult.success && leaderboardSettingsResult.data) {
                setMaxRewardsLimit(leaderboardSettingsResult.data.maxPerformers || 10);
            } else {
                setMaxRewardsLimit(10);
            }
            setSettingsLoaded(true);

            // Wait for reviews to complete
            await reviewsPromise;
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            setError(error.message || 'Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const fetchReviews = async (page = currentPage) => {
        try {
            // Build review parameters
            const reviewParams = {
                page: page,
                limit: reviewsPerPage,
                sortBy: sortBy,
                sortOrder: sortOrder
            };

            // Add filters if they exist
            if (searchTerm.trim()) reviewParams.search = searchTerm.trim();
            if (starRatingFilter) reviewParams.rating = starRatingFilter;
            if (assignmentFilter) reviewParams.assignmentStatus = assignmentFilter;
            if (responseFilter) reviewParams.processingStatus = responseFilter;

            const reviewsResult = await reviewsAPI.getReviews(reviewParams);

            // Update pagination state from API response
            setCurrentPage(reviewsResult.data.page);
            setTotalPages(reviewsResult.data.totalPages);
            setTotalReviews(reviewsResult.data.total);

            // Transform Google reviews data to match frontend format
            const transformedGoogleReviews = reviewsResult.data.reviews.map(googleReview => ({
                id: googleReview.id,
                customerName: googleReview.reviewerName || 'Anonymous',
                technicianName: googleReview.assignedTechnician?.name || 'Unknown',
                technicianCrmCode: googleReview.assignedTechnician?.crmCode || null,
                rating: googleReview.starRating,
                text: googleReview.comment || '',
                date: googleReview.createTime,
                sentiment: googleReview.starRating >= 4 ? 'positive' : googleReview.starRating === 3 ? 'neutral' : 'negative',
                responded: googleReview.responseText || googleReview.metadata?.reviewReply?.comment ? true : false,
                source: 'google',
                status: googleReview.processingStatus,
                published: googleReview.responseStatus === 'published' || googleReview.metadata?.reviewReply?.updateTime ? true : false,
                publishedAt: googleReview.responseDate || googleReview.metadata?.reviewReply?.updateTime,
                responseApprovalStatus: googleReview.responseApprovalStatus || 'pending',
                responseApprovedBy: googleReview.approvedBy || null,
                responseApprovedAt: googleReview.approvedAt || null,
                assignmentStatus: googleReview.assignmentStatus,
                // Add location information
                locationTitle: googleReview.locationTitle || null,
                locationAddress: googleReview.locationAddress || null,
                // Add AI response if it exists, checking both responseText and metadata
                aiResponse: googleReview.responseText ? {
                    text: googleReview.responseText,
                    generatedAt: googleReview.responseDate,
                    isFromGoogle: false
                } : googleReview.metadata?.reviewReply?.comment ? {
                    text: googleReview.metadata.reviewReply.comment,
                    generatedAt: googleReview.metadata.reviewReply.updateTime,
                    isFromGoogle: true
                } : null
            }));

            // Censor all review texts with AI
            const reviewsWithCensoredText = await Promise.all(
                transformedGoogleReviews.map(async (review) => ({
                    ...review,
                    censoredText: await censorReviewText(review.text)
                }))
            );

            // Set both reviews and googleReviews to the same data for backward compatibility
            setReviews(reviewsWithCensoredText);
            setGoogleReviews(reviewsWithCensoredText);
        } catch (error) {
            console.error('Error fetching reviews:', error);
            setError(error.message || 'Failed to load reviews');
        }
    };

    // Pagination handlers
    const handlePageChange = async (newPage) => {
        if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage && !paginationLoading) {
            setPaginationLoading(true);
            setCurrentPage(newPage);
            try {
                await fetchReviews(newPage);
            } finally {
                setPaginationLoading(false);
            }
        }
    };

    const handleLimitChange = (newLimit) => {
        setReviewsPerPage(newLimit);
        setCurrentPage(1); // Reset to first page
        fetchReviews(1);
    };

    // Filter handlers that trigger refetch
    const handleSearchChange = (value) => {
        setSearchTerm(value);
        setCurrentPage(1); // Reset to first page when searching
        // Debounce search - will be called by useEffect
    };

    const handleFilterChange = (filterType, value) => {
        switch(filterType) {
            case 'rating':
                setStarRatingFilter(value);
                break;
            case 'assignment':
                setAssignmentFilter(value);
                break;
            case 'response':
                setResponseFilter(value);
                break;
        }
        setCurrentPage(1); // Reset to first page when filtering
    };

    const handleSortChange = (field, order) => {
        setSortBy(field);
        setSortOrder(order);
        setCurrentPage(1); // Reset to first page when sorting
    };

    // Optimized search effect with faster response
    const hasSearchChangedRef = useRef(false);
    const searchTimeoutRef = useRef(null);
    
    useEffect(() => {
        // Skip only the very first render when searchTerm is empty
        if (searchTerm === '' && !hasSearchChangedRef.current) {
            return;
        }
        hasSearchChangedRef.current = true;
        
        // Clear existing timeout
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        
        searchTimeoutRef.current = setTimeout(() => {
            if (currentPage === 1) {
                fetchReviews(1);
            } else {
                setCurrentPage(1);
                fetchReviews(1);
            }
        }, 300); // Reduced from 500ms to 300ms

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm]);

    // Filter change effects - Optimized with immediate UI updates
    const isInitialRender = useRef(true);
    const filterTimeoutRef = useRef(null);
    
    useEffect(() => {
        if (isInitialRender.current) {
            isInitialRender.current = false;
            return;
        }
        
        // Clear any existing timeout
        if (filterTimeoutRef.current) {
            clearTimeout(filterTimeoutRef.current);
        }
        
        // Immediate loading state without delay
        setFilteringLoading(true);
        
        // Reduced debounce for faster response
        filterTimeoutRef.current = setTimeout(async () => {
            try {
                if (currentPage === 1) {
                    await fetchReviews(1);
                } else {
                    setCurrentPage(1);
                    await fetchReviews(1);
                }
            } finally {
                setFilteringLoading(false);
            }
        }, 150); // Reduced from 300ms to 150ms

        return () => {
            if (filterTimeoutRef.current) {
                clearTimeout(filterTimeoutRef.current);
            }
        };
    }, [starRatingFilter, assignmentFilter, responseFilter, sortBy, sortOrder]);

    const fetchTechnicianPersona = async (crmCode) => {
        try {
            const result = await techniciansAPI.getTechnicianByCrmCode(crmCode);

            if (result.success && result.data.persona) {
                const persona = result.data.persona;
                return {
                    traits: Array.isArray(persona.traits) ? persona.traits : [persona.traits || '', '', ''],
                    personality: typeof persona.personality === 'string'
                        ? persona.personality.split(' and ')
                        : [persona.personality || '', ''],
                    communicationStyle: typeof persona.communicationStyle === 'string'
                        ? persona.communicationStyle.split(' and ')
                        : [persona.communicationStyle || '', '']
                };
            }
            return {
                traits: ['', '', ''],
                personality: ['', ''],
                communicationStyle: ['', '']
            };
        } catch (error) {
            console.error('Error fetching technician persona:', error);
            return {
                traits: ['', '', ''],
                personality: ['', ''],
                communicationStyle: ['', '']
            };
        }
    };

    const handleMenuClick = async (reviewId, crmCode) => {
        if (openMenuId === reviewId) {
            setOpenMenuId(null);
            setEditingPersona(null);
        } else {
            setOpenMenuId(reviewId);
            if (crmCode) {
                const persona = await fetchTechnicianPersona(crmCode);
                setPersonaForm(persona);
            }
        }
    };

    const handleEditPersona = async (identifier) => {
        // If identifier is a CRM code (from profile dropdown), navigate to PersonaBuilder
        if (typeof identifier === 'string' && identifier.length <= 10) {
            const personaBuilderUrl = buildUrlWithParams('/persona-builder');
            navigate(personaBuilderUrl);
        } else {
            // Original behavior for review IDs - still use inline edit
            setEditingPersona(identifier);
        }
    };

    const handlePersonaInputChange = (field, index, value) => {
        setPersonaForm(prev => ({
            ...prev,
            [field]: prev[field].map((item, i) => i === index ? value : item)
        }));
    };

    const handleSavePersona = async (crmCode) => {
        if (!crmCode) {
            showError('Cannot update persona: Technician CRM code not found');
            return;
        }

        try {
            setSavingPersona(true);

            const result = await techniciansAPI.updatePersona(crmCode, {
                traits: personaForm.traits.filter(trait => trait.trim() !== ''),
                personality: personaForm.personality.filter(p => p.trim() !== '').join(' and '),
                communicationStyle: personaForm.communicationStyle.filter(cs => cs.trim() !== '').join(' and ')
            });

            showSuccess(`Persona updated successfully for ${result.data.name} (${crmCode})!`);
            setEditingPersona(null);
            setOpenMenuId(null);
            
            // Update local state instead of refetching all data
            const updatedPersona = {
                traits: personaForm.traits.filter(trait => trait.trim() !== ''),
                personality: personaForm.personality.filter(p => p.trim() !== '').join(' and '),
                communicationStyle: personaForm.communicationStyle.filter(cs => cs.trim() !== '').join(' and ')
            };
            
            // Update reviews state
            setReviews(prevReviews => 
                prevReviews.map(review => 
                    review.technicianCrmCode === crmCode 
                        ? { ...review, persona: updatedPersona }
                        : review
                )
            );
            
            // Update googleReviews state
            setGoogleReviews(prevReviews => 
                prevReviews.map(review => 
                    review.technicianCrmCode === crmCode 
                        ? { ...review, persona: updatedPersona }
                        : review
                )
            );
        } catch (error) {
            showError(`Error: ${error.message}`);
        } finally {
            setSavingPersona(false);
        }
    };

    const handleLogout = async () => {
        try {
            await logout();
            navigate(buildUrlWithParams('/login'));
        } catch (error) {
            console.error('Logout error:', error);
        }
    };

    const handleProfile = () => {
        // Navigate to profile with preserved URL parameters
        const profileUrl = buildUrlWithParams('/profile');
        navigate(profileUrl);
    };

    const handleAdminPortal = () => {
        // Navigate to admin portal with preserved URL parameters
        const adminPortalUrl = buildUrlWithParams('/admin/portal');
        navigate(adminPortalUrl);
    };

    const handleBankAccount = () => {
        // Navigate to bank account setup with preserved URL parameters
        const bankAccountUrl = buildUrlWithParams('/bank-account');
        navigate(bankAccountUrl);
    };

    const handleRedeemRewards = () => {
        // Navigate to redeem rewards with preserved URL parameters
        const redeemRewardsUrl = buildUrlWithParams('/redeem-rewards');
        navigate(redeemRewardsUrl);
    };

    const handleSoundToggle = () => {
        const newValue = !soundEnabled;
        setSoundEnabled(newValue);
        setNotificationSoundEnabled(newValue);
        // Initialize audio context on first enable (requires user interaction)
        if (newValue) {
            initializeNotifications();
            // Play a test sound so user knows it's working
            playNotificationSound();
        }
    };

    const toggleProfileDropdown = useMemo(() => () => {
        setProfileDropdownOpen(prev => !prev);
    }, []);

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const renderStars = (rating) => {
        return Array.from({ length: 5 }, (_, index) => (
            <Star
                key={index}
                className={`review-card__star ${
                    index < rating ? 'review-card__star--filled' : 'review-card__star--empty'
                }`}
                size={16}
            />
        ));
    };

    const getInitials = (name) => {
        return name
            .split(' ')
            .map(n => n[0])
            .join('')
            .toUpperCase();
    };

    const renderAvatar = useMemo(() => (user, className = '', size = null) => {
        const profilePhoto = user?.profilePhoto;
        const name = user?.name || 'Unknown';
        
        if (profilePhoto) {
            return (
                <img 
                    src={profilePhoto} 
                    alt={`${name}'s profile`}
                    className={className}
                    style={size ? { width: size, height: size } : {}}
                />
            );
        }
        
        return (
            <div className={className} style={size ? { width: size, height: size } : {}}>
                {getInitials(name)}
            </div>
        );
    }, []);

    // Memoize profile dropdown content to prevent re-renders (placed after renderAvatar definition)
    const profileTriggerContent = useMemo(() => {
        if (!technician) {
            return (
                <>
                    <div className="profile-dropdown__avatar"><User size={20} /></div>
                    <div className="profile-dropdown__info">
                        <span className="profile-dropdown__name">Unknown User</span>
                        <span className="profile-dropdown__code"></span>
                    </div>
                </>
            );
        }
        
        return (
            <>
                {renderAvatar(technician, "profile-dropdown__avatar")}
                <div className="profile-dropdown__info">
                    <span className="profile-dropdown__name">{technician.name}</span>
                    <span className="profile-dropdown__code">{technician.crmCode || ''}</span>
                </div>
            </>
        );
    }, [technician, renderAvatar]);
    
    // Memoize profile dropdown menu content
    const profileMenuContent = useMemo(() => {
        if (!technician) return null;
        
        return (
            <div className="profile-dropdown__menu-header">
                {renderAvatar(technician, "profile-dropdown__menu-avatar")}
                <div className="profile-dropdown__menu-info">
                    <div className="profile-dropdown__menu-name">{technician.name}</div>
                    <div className="profile-dropdown__menu-email">{technician.email || 'No email'}</div>
                </div>
            </div>
        );
    }, [technician, renderAvatar]);

    const isNegativeReview = (review) => {
        return review.rating <= 2 || review.sentiment === 'negative';
    };

    const canPublishResponse = (review) => {
        if (!review.aiResponse) return false;
        if (isNegativeReview(review)) {
            return review.responseApprovalStatus === 'approved';
        }
        return true;
    };

    // Server-side filtering is now handled in fetchDashboardData

    // Removed collapse functionality - AI responses always stay open for conversation view

    // Helper to calculate textarea rows based on content
    const calculateTextareaRows = (text) => {
        if (!text) return 3;
        const lines = text.split('\n').length;
        const estimatedLines = Math.ceil(text.length / 65);
        return Math.max(3, Math.min(8, Math.max(lines, estimatedLines)));
    };

    // Helper to calculate optimal bubble width based on text content
    const calculateBubbleWidth = (text) => {
        if (!text) return '280px';
        
        // Check for line breaks - if many lines, use more width
        const lines = text.split('\n').length;
        const avgLineLength = text.length / lines;
        
        // For very short text (< 50 chars), use minimal width
        if (text.length < 50) return '40%';
        // For short text (< 100 chars), use narrower width
        if (text.length < 100 && avgLineLength < 60) return '50%';
        // For medium text (< 200 chars), use medium width  
        if (text.length < 200) return '65%';
        // For longer text, use more width
        return '85%';
    };

    // Helper specifically for review text width (customer reviews tend to be longer)
    const calculateReviewWidth = (text) => {
        if (!text) return '200px';
        
        // Reviews often have more varied lengths, so be more conservative
        const lines = text.split('\n').length;
        const avgLineLength = text.length / lines;
        
        // For very short reviews (< 80 chars), use smaller width
        if (text.length < 80) return '45%';
        // For short reviews (< 150 chars), use medium width
        if (text.length < 150) return '60%';
        // For medium reviews (< 300 chars), use larger width  
        if (text.length < 300) return '75%';
        // For long reviews, use most available width
        return '90%';
    };

    const generateAIResponse = async (reviewId) => {
        try {
            setLoadingResponses(prev => ({ ...prev, [reviewId]: true }));

            const result = await reviewsAPI.generateResponse(reviewId);
            
            // Update the AI response in both states for backward compatibility
            setGoogleReviews(prevGoogleReviews => 
                prevGoogleReviews.map(review => 
                    review.id === reviewId 
                        ? { 
                            ...review, 
                            aiResponse: {
                                text: result.data.response,
                                generatedAt: new Date().toISOString(),
                                status: result.data.responseApprovalStatus || 'pending',
                                isFromGoogle: false
                            },
                            responded: true
                        }
                        : review
                )
            );
            
            setReviews(prevReviews => 
                prevReviews.map(review => 
                    review.id === reviewId 
                        ? { 
                            ...review, 
                            aiResponse: {
                                text: result.data.response,
                                generatedAt: new Date().toISOString(),
                                status: result.data.responseApprovalStatus || 'pending',
                                isFromGoogle: false
                            },
                            responded: true
                        }
                        : review
                )
            );
            
            showSuccess('AI response generated successfully!');
        } catch (error) {
            showError(`Error: ${error.message}`);
        } finally {
            setLoadingResponses(prev => ({ ...prev, [reviewId]: false }));
        }
    };

    const approveResponse = async (reviewId) => {
        try {
            setLoadingResponseApprovals(prev => ({ ...prev, [reviewId]: true }));

            const result = await reviewsAPI.approveResponse(reviewId, 'current_user');
            await fetchDashboardData();
            showSuccess('Response approved successfully!');
        } catch (error) {
            showError(`Error: ${error.message}`);
        } finally {
            setLoadingResponseApprovals(prev => ({ ...prev, [reviewId]: false }));
        }
    };

    const publishResponse = async (reviewId) => {
        try {
            const result = await reviewsAPI.publishResponse(reviewId);
            
            // Update local state instead of refreshing entire page
            setReviews(prevReviews =>
                prevReviews.map(review =>
                    review.id === reviewId
                        ? {
                            ...review,
                            published: true,
                            publishedAt: new Date().toISOString()
                        }
                        : review
                )
            );
            
            setGoogleReviews(prevReviews =>
                prevReviews.map(review =>
                    review.id === reviewId
                        ? {
                            ...review,
                            published: true,
                            publishedAt: new Date().toISOString()
                        }
                        : review
                )
            );
            
            showSuccess('Response published successfully!');
        } catch (error) {
            showError(`Error publishing response: ${error.message}`);
        }
    };

    const editResponse = (reviewId, newText) => {
        setReviews(prevReviews =>
            prevReviews.map(review =>
                review.id === reviewId
                    ? {
                        ...review,
                        aiResponse: {
                            ...review.aiResponse,
                            text: newText
                        }
                    }
                    : review
            )
        );
    };

    const renderReviewActions = (review) => {
        // Only show generate response button if there's no AI response
        if (!review.aiResponse) {
            return (
                <button
                    className="review-card__respond-btn"
                    onClick={() => generateAIResponse(review.id)}
                    disabled={loadingResponses[review.id]}
                >
                    {loadingResponses[review.id] ? 'Generating...' : 'Generate Response'}
                </button>
            );
        }
        // If there's an AI response, don't show anything here (status is now in ai-response header)
        return null;
    };

    const renderAIResponseActions = (review) => {
        const isNegative = isNegativeReview(review);
        const needsApproval = isNegative && review.responseApprovalStatus === 'pending' && userRole !== 'technician';
        const isApproved = review.responseApprovalStatus === 'approved';
        const isPublished = review.published;
        const isFromGoogle = review.aiResponse?.isFromGoogle;

        // For Google responses or published AI responses, show only technician info
        if (isFromGoogle || isPublished) {
            return (
                <div className="ai-response__actions">
                    {/* Technician info on the left */}
                    <div className="ai-response__footer">
                        <div className="ai-response__technician-info">
                            {review.technicianName !== 'Unknown' ? (
                                <>
                                    {renderAvatar(
                                        { name: review.technicianName, profilePhoto: review.technicianProfilePhoto },
                                        "ai-response__tech-avatar"
                                    )}
                                    <span className="ai-response__tech-name">{anonymizeName(review.technicianName)}</span>
                                </>
                            ) : (
                                <span className="ai-response__tech-unknown">
                                    {review.source === 'google' ? 'Unassigned' : 'Technician not identified'}
                                </span>
                            )}
                        </div>
                        {(isAdmin() || isManager()) && (
                            <div className="ai-response__menu-container" ref={openMenuId === review.id ? menuRef : null}>
                                <button
                                    className="ai-response__menu-btn"
                                    onClick={() => handleMenuClick(review.id, review.technicianCrmCode)}
                                >
                                    <MoreVertical size={16} />
                                </button>
                                <AnimatePresence>
                                {openMenuId === review.id && (
                                    <motion.div
                                        className="menu-dropdown"
                                        initial={{ opacity: 0, scale: 0.95, y: -10 }}
                                        animate={{ opacity: 1, scale: 1, y: 0 }}
                                        exit={{ opacity: 0, scale: 0.95, y: -10 }}
                                        transition={{ duration: 0.15 }}
                                    >
                                        {renderPersonaMenu(review)}
                                    </motion.div>
                                )}
                                </AnimatePresence>
                            </div>
                        )}
                    </div>
                    {isPublished && (
                        <div className="ai-response__buttons">
                            <span className="ai-response__approval-status ai-response__approval-status--approved">
                                <CheckCircle size={14} />
                                Response Published
                            </span>
                        </div>
                    )}
                </div>
            );
        }

        return (
            <div className="ai-response__actions">
                {/* Technician info on the left */}
                <div className="ai-response__footer">
                    <div className="ai-response__technician-info">
                        {review.technicianName !== 'Unknown' ? (
                            <>
                                {renderAvatar(
                                    { name: review.technicianName, profilePhoto: review.technicianProfilePhoto },
                                    "ai-response__tech-avatar"
                                )}
                                <span className="ai-response__tech-name">{anonymizeName(review.technicianName)}</span>
                            </>
                        ) : (
                            <span className="ai-response__tech-unknown">
                                {review.source === 'google' ? 'Unassigned' : 'Technician not identified'}
                            </span>
                        )}
                    </div>
                    {(isAdmin() || isManager()) && (
                        <div className="ai-response__menu-container" ref={openMenuId === review.id ? menuRef : null}>
                            <button
                                className="ai-response__menu-btn"
                                onClick={() => handleMenuClick(review.id, review.technicianCrmCode)}
                            >
                                <MoreVertical size={16} />
                            </button>
                            <AnimatePresence>
                            {openMenuId === review.id && (
                                <motion.div
                                    className="menu-dropdown"
                                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                                    transition={{ duration: 0.15 }}
                                >
                                    {renderPersonaMenu(review)}
                                </motion.div>
                            )}
                            </AnimatePresence>
                        </div>
                    )}
                </div>
                
                {/* Action buttons on the right */}
                <div className="ai-response__buttons">
                    <button
                        className="ai-response__regenerate-btn"
                        onClick={() => generateAIResponse(review.id)}
                        disabled={loadingResponses[review.id]}
                    >
                        Regenerate
                    </button>

                    {needsApproval && (
                        <button
                            className="ai-response__approve-btn"
                            onClick={() => approveResponse(review.id)}
                            disabled={loadingResponseApprovals[review.id]}
                        >
                            <ThumbsUp size={14} />
                            {loadingResponseApprovals[review.id] ? 'Approving...' : 'Approve Response'}
                        </button>
                    )}

                    {isNegative && isApproved && (
                        <span className="ai-response__approval-status ai-response__approval-status--approved">
                            <CheckCircle size={14} />
                            Response Approved
                        </span>
                    )}

                    {canPublishResponse(review) && (
                        <button
                            className="ai-response__publish-btn"
                            onClick={() => publishResponse(review.id)}
                        >
                            <Send size={14} />
                            Publish Response
                        </button>
                    )}
                </div>
            </div>
        );
    };

    const refreshTopTechnicians = async () => {
        try {
            const topTechResult = await dashboardAPI.getTopTechnicians();
            setTopTechnicians(topTechResult.data);
        } catch (error) {
            console.error('Error refreshing top technicians:', error);
        }
    };

    const assignGoogleReview = async (reviewId, crmCode) => {
        try {
            const result = await reviewsAPI.assignReview(reviewId, crmCode);
            if (result.success) {
                // Get technician details
                const technicianResult = await techniciansAPI.getTechnicianByCrmCode(crmCode);
                
                // Update local state instead of refetching everything
                setReviews(prevReviews => 
                    prevReviews.map(review => 
                        review.id === reviewId 
                            ? {
                                ...review,
                                technicianName: technicianResult.data?.name || 'Unknown',
                                technicianCrmCode: crmCode,
                                assignmentStatus: 'assigned'
                            }
                            : review
                    )
                );
                
                setGoogleReviews(prevReviews => 
                    prevReviews.map(review => 
                        review.id === reviewId 
                            ? {
                                ...review,
                                technicianName: technicianResult.data?.name || 'Unknown',
                                technicianCrmCode: crmCode,
                                assignmentStatus: 'assigned'
                            }
                            : review
                    )
                );
                
                // Refresh top technicians leaderboard after successful assignment
                await refreshTopTechnicians();
                
                showSuccess('Google review assigned successfully!');
            }
        } catch (error) {
            showError(`Error assigning review: ${error.message}`);
            throw error; // Re-throw so the modal knows there was an error
        }
    };

    const handleAssignGoogleReviewClick = (review) => {
        setReviewToAssign(review);
        setShowAssignModal(true);
    };

    const handleAssignGoogleReviewSubmit = async (crmCode) => {
        if (!reviewToAssign) return;
        await assignGoogleReview(reviewToAssign.id, crmCode);
    };

    const handleAssignModalClose = () => {
        setShowAssignModal(false);
        setReviewToAssign(null);
    };

    const handleReassignGoogleReviewClick = (review) => {
        setReviewToReassign(review);
        setShowReassignConfirmModal(true);
    };

    const handleReassignConfirm = () => {
        if (!reviewToReassign) return;
        setShowReassignConfirmModal(false);
        setReviewToAssign(reviewToReassign);
        setShowAssignModal(true);
        setReviewToReassign(null);
    };

    const handleReassignCancel = () => {
        setShowReassignConfirmModal(false);
        setReviewToReassign(null);
    };

    const handleEditMaxRewards = () => {
        setShowRewardsDropdown(false);
        setTempMaxRewardsLimit(maxRewardsLimit);
        setTempMaxRewardAmount(maxRewardAmount);
        setTempMinimumPayoutThreshold(minimumPayoutThreshold);
        setTempRewardAmountPerReview(rewardAmountPerReview);
        setShowRewardsModal(true);
    };

    const handleSaveMaxRewards = async () => {
        try {
            // Save payment settings and leaderboard settings in parallel
            const [rewardResult, leaderboardResult] = await Promise.all([
                dashboardAPI.updateRewardSettings({
                    maxRewardAmount: parseInt(tempMaxRewardAmount) || 0,
                    minimumPayoutThreshold: parseFloat(tempMinimumPayoutThreshold) || 50,
                    rewardAmountPerReview: parseFloat(tempRewardAmountPerReview) || 5
                }),
                dashboardAPI.updateLeaderboardSettings({
                    maxPerformers: parseInt(tempMaxRewardsLimit) || 1,
                    maxRewardAmount: parseInt(tempMaxRewardAmount) || 0
                })
            ]);

            if (rewardResult.success && leaderboardResult.success) {
                setMaxRewardsLimit(parseInt(tempMaxRewardsLimit) || 1);
                setMaxRewardAmount(parseInt(tempMaxRewardAmount) || 0);
                setMinimumPayoutThreshold(parseFloat(tempMinimumPayoutThreshold) || 50);
                setRewardAmountPerReview(parseFloat(tempRewardAmountPerReview) || 5);
                setShowRewardsModal(false);
                showSuccess(`Reward settings updated! All reward calculations refreshed.`);

                // Automatically refresh data to show updated reward calculations
                await refreshDashboardData();
            } else {
                showError(rewardResult.message || leaderboardResult.message || 'Failed to update reward settings');
            }
        } catch (error) {
            showError(error.message || 'Failed to update reward settings');
        }
    };

    const handleCancelMaxRewards = () => {
        setTempMaxRewardsLimit(maxRewardsLimit);
        setTempMaxRewardAmount(maxRewardAmount);
        setTempMinimumPayoutThreshold(minimumPayoutThreshold);
        setTempRewardAmountPerReview(rewardAmountPerReview);
        setShowRewardsModal(false);
    };

    const refreshDashboardData = async () => {
        try {
            const [statsResult, topTechResult] = await Promise.all([
                dashboardAPI.getStats(),
                dashboardAPI.getTopTechnicians()
            ]);

            setStats(statsResult.data);
            setTopTechnicians(topTechResult.data);
        } catch (error) {
            console.error('Error refreshing dashboard data:', error);
            showError('Failed to refresh dashboard data. Please refresh the page to see updated rewards.');
        }
    };

    const renderPersonaMenu = (review) => {
        if (editingPersona === review.id) {
            return (
                <div className="persona-edit-form">
                    <div className="persona-edit-form__header">
                        <h4>Edit {review.technicianName}'s Persona</h4>
                        <button
                            className="persona-edit-form__close"
                            onClick={() => setEditingPersona(null)}
                        >
                            <X size={16} />
                        </button>
                    </div>

                    <div className="persona-edit-form__header">
                        <button
                            className="persona-edit-form__close"
                            onClick={() => {
                                const promptsUrl = buildUrlWithParams('/admin/prompts');
                                navigate(promptsUrl);
                            }}
                        >
                            Prompt Management
                        </button>
                    </div>

                    <div className="persona-edit-form__section">
                        <label className="persona-edit-form__label">Traits</label>
                        {personaForm.traits.map((trait, index) => (
                            <input
                                key={index}
                                type="text"
                                className="persona-edit-form__input"
                                placeholder={`Trait ${index + 1}`}
                                value={trait}
                                onChange={(e) => handlePersonaInputChange('traits', index, e.target.value)}
                            />
                        ))}
                    </div>

                    <div className="persona-edit-form__section">
                        <label className="persona-edit-form__label">Personality</label>
                        {personaForm.personality.map((personality, index) => (
                            <input
                                key={index}
                                type="text"
                                className="persona-edit-form__input"
                                placeholder={`Personality ${index + 1}`}
                                value={personality}
                                onChange={(e) => handlePersonaInputChange('personality', index, e.target.value)}
                            />
                        ))}
                    </div>

                    <div className="persona-edit-form__section">
                        <label className="persona-edit-form__label">Communication Style</label>
                        {personaForm.communicationStyle.map((style, index) => (
                            <input
                                key={index}
                                type="text"
                                className="persona-edit-form__input"
                                placeholder={`Communication Style ${index + 1}`}
                                value={style}
                                onChange={(e) => handlePersonaInputChange('communicationStyle', index, e.target.value)}
                            />
                        ))}
                    </div>

                    <div className="persona-edit-form__actions">
                        <button
                            className="persona-edit-form__save-btn"
                            onClick={() => handleSavePersona(review.technicianCrmCode)}
                            disabled={savingPersona}
                        >
                            <Save size={14} />
                            {savingPersona ? 'Saving...' : 'Save Persona'}
                        </button>
                    </div>
                </div>
            );
        }

        return (
            <div className="dropdown-menu">
                {review.source === 'google' && review.technicianName === 'Unknown' && (isAdmin() || isManager()) && (
                    <button
                        className="dropdown-menu__item"
                        onClick={() => handleAssignGoogleReviewClick(review)}
                    >
                        <Users size={14} />
                        Assign to Technician
                    </button>
                )}
                {review.source === 'google' && review.technicianName !== 'Unknown' && (isAdmin() || isManager()) && (
                    <button
                        className="dropdown-menu__item"
                        onClick={() => handleReassignGoogleReviewClick(review)}
                    >
                        <Users size={14} />
                        Reassign to Different Technician
                    </button>
                )}
            </div>
        );
    };

    // Render top technicians section
    const renderTopTechnicians = () => {
        if (topTechnicians.length === 0) {
            return (
                <div className="top-technicians__empty">
                    <Trophy size={48} className="top-technicians__empty-icon" />
                    <p>No technician performance data available yet.</p>
                </div>
            );
        }

        return (
            <div className="top-technicians__list">
                {topTechnicians.slice(0, maxRewardsLimit).map((tech, index) => (
                    <motion.div
                        key={tech.id}
                        className={`top-tech-card ${index === 0 ? 'top-tech-card--first' : ''}`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                    >
                        <div className="top-tech-card__rank">
                            {index === 0 && <Trophy size={20} className="top-tech-card__trophy" />}
                            <span className="top-tech-card__rank-number">#{index + 1}</span>
                        </div>

                        {renderAvatar(tech, "top-tech-card__avatar")}

                        <div className="top-tech-card__info">
                            <div className="top-tech-card__name">{anonymizeName(tech.name)}</div>
                            <div className="top-tech-card__code">{tech.crmCode}</div>
                        </div>

                        <div className="top-tech-card__stats">
                            <div className="top-tech-card__stat">
                                <Star size={16} className="top-tech-card__stat-icon" />
                                <span className="top-tech-card__stat-value">{tech.averageRating}</span>
                                <span className="top-tech-card__stat-label">Avg Rating</span>
                            </div>

                            <div className="top-tech-card__stat">
                                <ThumbsUp size={16} className="top-tech-card__stat-icon" />
                                <span className="top-tech-card__stat-value">{tech.positiveReviews}</span>
                                <span className="top-tech-card__stat-label">Positive Reviews</span>
                            </div>

                            <div className="top-tech-card__stat">
                                <DollarSign size={16} className="top-tech-card__stat-icon" />
                                <span className="top-tech-card__stat-value">${Math.min(tech.totalRewards, maxRewardAmount)}</span>
                                <span className="top-tech-card__stat-label">Rewards</span>
                            </div>
                        </div>
                    </motion.div>
                ))}
            </div>
        );
    };

    if (loading) {
        return (
            <div className="dashboard dashboard--loading">
                <div className="loading-spinner">
                    <div className="spinner"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard dashboard--error">
                <div className="error-message">
                    <h2>Failed to load dashboard</h2>
                    <p>{error}</p>
                    <button onClick={fetchDashboardData} className="retry-btn">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="dashboard">
            {/* Header Section */}
            <div className="dashboard__header">
                <div className="dashboard__title-section">
                    <img src="/ElevateLogo.svg" alt="Elevate" className="dashboard__logo" />
                    <p className="dashboard__subtitle">Monitor and manage technician reviews</p>
                </div>

                <div className="dashboard__actions">
                    <button
                        type="button"
                        className="dashboard__refresh-btn"
                        onClick={async (e) => {
                            e.preventDefault();
                            // Trigger auto-match to match technicians with reviews, then refresh
                            try {
                                await adminAPI.triggerAutoMatch();
                            } catch (err) {
                                console.log('Auto-match trigger failed (may require manager role):', err.message);
                            }
                            fetchReviews();
                        }}
                    >
                        Refresh Data
                    </button>

                    <button
                        type="button"
                        className={`dashboard__sound-btn ${soundEnabled ? 'dashboard__sound-btn--on' : 'dashboard__sound-btn--off'}`}
                        onClick={handleSoundToggle}
                        title={soundEnabled ? 'Sound notifications enabled' : 'Sound notifications disabled'}
                    >
                        {soundEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
                        {soundEnabled ? 'Sound On' : 'Sound Off'}
                    </button>

                    {/* Profile Dropdown */}
                    <div className="profile-dropdown" ref={profileDropdownRef}>
                        <button
                            className="profile-dropdown__trigger"
                            onClick={toggleProfileDropdown}
                        >
                            {profileTriggerContent}
                            <ChevronDown
                                size={16}
                                className={`profile-dropdown__chevron ${profileDropdownOpen ? 'profile-dropdown__chevron--open' : ''}`}
                            />
                        </button>

                        {profileDropdownOpen && (
                            <div className="profile-dropdown__menu">
                                    {profileMenuContent}

                                    <div className="profile-dropdown__menu-divider"></div>

                                    <button
                                        className="profile-dropdown__menu-item"
                                        onClick={handleProfile}
                                    >
                                        <User size={16} />
                                        <span>Profile</span>
                                    </button>

                                    <button
                                        className="profile-dropdown__menu-item"
                                        onClick={handleBankAccount}
                                    >
                                        <Building2 size={16} />
                                        <span>Bank Account</span>
                                    </button>

                                    <button
                                        className="profile-dropdown__menu-item"
                                        onClick={handleRedeemRewards}
                                    >
                                        <DollarSign size={16} />
                                        <span>Redeem Rewards</span>
                                    </button>

                                    <button
                                        className="profile-dropdown__menu-item"
                                        onClick={() => {
                                            handleEditPersona(technician.crmCode);
                                            setProfileDropdownOpen(false);
                                        }}
                                        disabled={!technician.crmCode}
                                    >
                                        <Edit3 size={16} />
                                        <span>Edit Persona</span>
                                    </button>

                                    <button
                                        className="profile-dropdown__menu-item"
                                        onClick={() => {
                                            const promptsUrl = buildUrlWithParams('/admin/prompts');
                                            navigate(promptsUrl);
                                            setProfileDropdownOpen(false);
                                        }}
                                    >
                                        <MessageSquare size={16} />
                                        <span>Prompt Management</span>
                                    </button>

                                    {(isAdmin() || isManager()) && (
                                        <button
                                            className="profile-dropdown__menu-item"
                                            onClick={handleAdminPortal}
                                        >
                                            <Shield size={16} />
                                            <span>Admin Portal</span>
                                        </button>
                                    )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="dashboard__stats">
                <motion.div className="stat-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                    <div className="stat-card__icon stat-card__icon--reviews">
                        <MessageSquare size={24} />
                    </div>
                    <div className="stat-card__content">
                        <div className="stat-card__value">{stats.totalReviews.toLocaleString()}</div>
                        <div className="stat-card__label">Total Reviews</div>
                    </div>
                </motion.div>

                <motion.div className="stat-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                    <div className="stat-card__icon stat-card__icon--rating">
                        <Star size={24} />
                    </div>
                    <div className="stat-card__content">
                        <div className="stat-card__value">{stats.averageRating}</div>
                        <div className="stat-card__label">Average Rating</div>
                    </div>
                </motion.div>

                <motion.div className="stat-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                    <div className="stat-card__icon stat-card__icon--technicians">
                        <Users size={24} />
                    </div>
                    <div className="stat-card__content">
                        <div className="stat-card__value">{stats.activeTechnicians}</div>
                        <div className="stat-card__label">Active Technicians</div>
                    </div>
                </motion.div>

                <motion.div className="stat-card" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
                    <div className="stat-card__icon stat-card__icon--rewards">
                        <DollarSign size={24} />
                    </div>
                    <div className="stat-card__content">
                        <div className="stat-card__value">${stats.totalRewards.toLocaleString()}</div>
                        <div className="stat-card__label">Total Rewards</div>
                    </div>
                </motion.div>
            </div>

            {/* Top Technicians Section */}
            <motion.div
                className="top-technicians"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
            >
                <div className="top-technicians__header">
                    <div className="top-technicians__title-section">
                        <div className="top-technicians__icon">
                            <Award size={24} />
                        </div>
                        <div>
                            <h2 className="top-technicians__title">Top Performers</h2>
                            <p className="top-technicians__subtitle">Ranked by positive reviews and customer satisfaction</p>
                        </div>
                    </div>
                    <div className="top-technicians__actions">
                        <div className="top-technicians__badge">
                            <TrendingUp size={16} />
                            <span>This Month</span>
                        </div>
                        {isAdmin() && (
                            <div className="rewards-dropdown" ref={rewardsDropdownRef}>
                                <button
                                    className="rewards-dropdown__trigger"
                                    onClick={() => setShowRewardsDropdown(!showRewardsDropdown)}
                                    aria-label="More options"
                                >
                                    <MoreVertical size={20} />
                                </button>
                                {showRewardsDropdown && (
                                    <div className="rewards-dropdown__menu">
                                        <button
                                            className="rewards-dropdown__item"
                                            onClick={handleEditMaxRewards}
                                        >
                                            <Settings size={16} />
                                            <span>Edit Max Rewards</span>
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="top-technicians__content">
                    {renderTopTechnicians()}
                </div>
            </motion.div>

            {/* Reviews Section */}
            <div className="dashboard__reviews">
                <div className="reviews-section">
                    <div className="reviews-section__header">
                        <div className="reviews-section__title-area">
                            <h2 className="reviews-section__title">Reviews ({totalReviews})</h2>
                        </div>

                        <form 
                            className="reviews-section__controls"
                            onSubmit={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                return false;
                            }}
                        >
                            <div className="search-input">
                                <Search className="search-input__icon" size={20} />
                                <input
                                    type="text"
                                    className="search-input__field"
                                    placeholder="Search reviews..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        e.preventDefault();
                                        handleSearchChange(e.target.value);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault();
                                            e.stopPropagation();
                                        }
                                    }}
                                />
                            </div>

                            <div className="filter-dropdown">
                                <button 
                                    type="button"
                                    className="filter-dropdown__trigger"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setFilterDropdownOpen(!filterDropdownOpen);
                                    }}
                                >
                                    <Filter size={16} />
                                    Filter
                                </button>
                                
                                {filterDropdownOpen && (
                                    <div 
                                        className="filter-dropdown__content"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="filter-dropdown__section">
                                            <label className="filter-dropdown__label">Star Rating:</label>
                                            <select
                                                className="filter-dropdown__select"
                                                value={starRatingFilter}
                                                onChange={(e) => {
                                                    e.preventDefault();
                                                    handleFilterChange('rating', e.target.value);
                                                }}
                                            >
                                                {starRatingOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Assignment Filter (only for admins/managers) */}
                                        {canManageAssignments && (
                                            <div className="filter-dropdown__section">
                                                <label className="filter-dropdown__label">Assignment:</label>
                                                <select
                                                    className="filter-dropdown__select"
                                                    value={assignmentFilter}
                                                    onChange={(e) => {
                                                        e.preventDefault();
                                                        handleFilterChange('assignment', e.target.value);
                                                    }}
                                                >
                                                    {assignmentOptions.map(option => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        {/* Response Filter */}
                                        <div className="filter-dropdown__section">
                                            <label className="filter-dropdown__label">Response Status:</label>
                                            <select
                                                className="filter-dropdown__select"
                                                value={responseFilter}
                                                onChange={(e) => {
                                                    e.preventDefault();
                                                    handleFilterChange('response', e.target.value);
                                                }}
                                            >
                                                {responseOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="filter-dropdown__actions">
                                            <button 
                                                type="button"
                                                className="filter-dropdown__clear"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setStarRatingFilter('');
                                                    setAssignmentFilter('');
                                                    setResponseFilter('');
                                                }}
                                            >
                                                Clear Filters
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </form>
                    </div>

                    <div className="reviews-section__content">
                        {filteringLoading && (
                            <div className="filtering-loading">
                                <div className="spinner"></div>
                                <span>Applying filters...</span>
                            </div>
                        )}
                        <div className="reviews-list">
                            {reviews.map((review, index) => (
                                <motion.div
                                    key={`${review.source || 'review'}-${review.id}`}
                                    className={`review-card review-card--${review.sentiment} ${
                                        isNegativeReview(review) ? 'review-card--flagged' : ''
                                    }`}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: index * 0.1 }}
                                >
                                    <div className="review-card__header">
                                        <div className="review-card__customer">
                                            <div className="review-card__avatar">
                                                {getInitials(anonymizeName(review.customerName))}
                                            </div>
                                            <div className="review-card__customer-info">
                                                <div className="review-card__customer-name">{anonymizeName(review.customerName)}</div>
                                                <div className="review-card__date">
                                                    {formatDate(review.date)}
                                                    {(review.locationTitle || review.locationAddress) && (
                                                        <span className="review-card__location-separator"> • </span>
                                                    )}
                                                    {review.locationTitle && (
                                                        <span className="review-card__location-business">{anonymizeCompanyName(review.locationTitle)}</span>
                                                    )}
                                                    {review.locationTitle && review.locationAddress && (
                                                        <span className="review-card__location-separator-inner"> - </span>
                                                    )}
                                                    {review.locationAddress && (
                                                        <span className="review-card__location-address">{anonymizeAddressString(review.locationAddress)}</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="review-card__rating">
                                            {renderStars(review.rating)}
                                        </div>

                                        <div className="review-card__source">
                                            <span className="review-card__source-text">
                                                {review.source === 'google' ? 'Google' : 'Platform'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="review-card__content">
                                        {review.text && review.text.trim() ? (
                                            <p
                                                className="review-card__text"
                                                style={{
                                                    width: calculateReviewWidth(review.text)
                                                }}
                                            >
                                                {review.censoredText || review.text}
                                            </p>
                                        ) : (
                                            <div className="review-card__no-text">
                                                <AlertCircle size={16} />
                                                <span>No review text has been added</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Show review card footer only if no AI response */}
                                    {!review.aiResponse && (
                                        <div className="review-card__footer">
                                            <div className="review-card__technician-section">
                                                <div className="review-card__technician">
                                                    {review.technicianName !== 'Unknown' ? (
                                                        <>
                                                            {renderAvatar(
                                                                { name: review.technicianName, profilePhoto: review.technicianProfilePhoto },
                                                                "review-card__tech-avatar"
                                                            )}
                                                            <span className="review-card__tech-name">{anonymizeName(review.technicianName)}</span>
                                                        </>
                                                    ) : (
                                                        <span className="review-card__tech-unknown">
                                                            {review.source === 'google' ? 'Unassigned Google Review' : 'Technician not identified'}
                                                        </span>
                                                    )}
                                                </div>
                                                
                                                {(isAdmin() || isManager()) && (
                                                    <div className="menu-container" ref={openMenuId === review.id ? menuRef : null}>
                                                        <button
                                                            className="review-card__menu-btn"
                                                            onClick={() => handleMenuClick(review.id, review.technicianCrmCode)}
                                                        >
                                                            <MoreVertical size={16} />
                                                        </button>

                                                    <AnimatePresence>
                                                        {openMenuId === review.id && (
                                                            <motion.div
                                                                className="menu-dropdown"
                                                                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                                                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                                                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                                                                transition={{ duration: 0.15 }}
                                                            >
                                                                {renderPersonaMenu(review)}
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="review-card__actions">
                                                {renderReviewActions(review)}
                                            </div>
                                        </div>
                                    )}

                                    {/* AI Response Section - Always Open */}
                                    {review.aiResponse && (
                                        <div className="ai-response-wrapper">
                                            <div className="ai-response__header">
                                                <div className="ai-response__icon">
                                                    {review.aiResponse.isFromGoogle ? <UserCircle size={16} /> : <Bot size={16} />}
                                                </div>
                                                <span className="ai-response__tech-name">
                                                    {review.technicianName !== 'Unknown' ? anonymizeName(review.technicianName) : 'AI Assistant'}
                                                </span>
                                                <span className="ai-response__timestamp">
                                                    {formatDate(review.aiResponse.generatedAt)}
                                                </span>
                                                {review.technicianName !== 'Unknown' && renderAvatar(
                                                    { name: review.technicianName, profilePhoto: review.technicianProfilePhoto }, 
                                                    "ai-response__avatar"
                                                )}
                                                {(isAdmin() || isManager()) && (
                                                    <div className="menu-container" ref={openMenuId === `response-${review.id}` ? menuRef : null}>
                                                        <button
                                                            className="ai-response__menu-btn"
                                                            onClick={() => handleMenuClick(`response-${review.id}`, review.technicianCrmCode)}
                                                        >
                                                            <MoreVertical size={16} />
                                                        </button>

                                                    <AnimatePresence>
                                                        {openMenuId === `response-${review.id}` && (
                                                            <motion.div
                                                                className="menu-dropdown"
                                                                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                                                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                                                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                                                                transition={{ duration: 0.15 }}
                                                            >
                                                                {renderPersonaMenu(review)}
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                    </div>
                                                )}
                                            </div>

                                            <motion.div
                                                className="ai-response"
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ duration: 0.3 }}
                                                style={{
                                                    width: calculateBubbleWidth(review.aiResponse.text)
                                                }}
                                            >
                                                <div className="ai-response__content">
                                                    <textarea
                                                        className={`ai-response__text ${review.aiResponse.isFromGoogle || review.published ? 'ai-response__text--readonly' : ''}`}
                                                        value={censorAIResponse(review.aiResponse.text, review.customerName, review.technicianName)}
                                                        onChange={(e) => !review.aiResponse.isFromGoogle && !review.published && editResponse(review.id, e.target.value)}
                                                        readOnly={review.aiResponse.isFromGoogle || review.published}
                                                        rows={calculateTextareaRows(review.aiResponse.text)}
                                                    />
                                                </div>

                                                {/* Only show actions if not published */}
                                                {!review.published && !review.aiResponse.isFromGoogle && renderAIResponseActions(review)}
                                            </motion.div>
                                        </div>
                                    )}
                                </motion.div>
                            ))}
                        </div>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="pagination-controls">
                                <div className="pagination-info">
                                    Showing {((currentPage - 1) * reviewsPerPage) + 1} - {Math.min(currentPage * reviewsPerPage, totalReviews)} of {totalReviews} reviews
                                </div>
                                
                                <div className="pagination-buttons">
                                    <button 
                                        type="button"
                                        className={`pagination-btn ${currentPage === 1 || paginationLoading ? 'disabled' : ''}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handlePageChange(1);
                                        }}
                                        disabled={currentPage === 1 || paginationLoading}
                                    >
                                        {paginationLoading ? '...' : 'First'}
                                    </button>
                                    
                                    <button 
                                        type="button"
                                        className={`pagination-btn ${currentPage === 1 || paginationLoading ? 'disabled' : ''}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handlePageChange(currentPage - 1);
                                        }}
                                        disabled={currentPage === 1 || paginationLoading}
                                    >
                                        <ChevronLeft size={16} />
                                        {paginationLoading ? 'Loading...' : 'Previous'}
                                    </button>
                                    
                                    <div className="page-numbers">
                                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 3) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= totalPages - 2) {
                                                pageNum = totalPages - 4 + i;
                                            } else {
                                                pageNum = currentPage - 2 + i;
                                            }
                                            
                                            return (
                                                <button
                                                    key={pageNum}
                                                    type="button"
                                                    className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        handlePageChange(pageNum);
                                                    }}
                                                    disabled={paginationLoading}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    
                                    <button 
                                        type="button"
                                        className={`pagination-btn ${currentPage === totalPages || paginationLoading ? 'disabled' : ''}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handlePageChange(currentPage + 1);
                                        }}
                                        disabled={currentPage === totalPages || paginationLoading}
                                    >
                                        {paginationLoading ? 'Loading...' : 'Next'}
                                        <ChevronRight size={16} />
                                    </button>
                                    
                                    <button 
                                        type="button"
                                        className={`pagination-btn ${currentPage === totalPages || paginationLoading ? 'disabled' : ''}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handlePageChange(totalPages);
                                        }}
                                        disabled={currentPage === totalPages || paginationLoading}
                                    >
                                        {paginationLoading ? '...' : 'Last'}
                                    </button>
                                </div>
                                
                                <div className="items-per-page">
                                    <label>Items per page:</label>
                                    <select 
                                        value={reviewsPerPage} 
                                        onChange={(e) => {
                                            e.preventDefault();
                                            handleLimitChange(parseInt(e.target.value));
                                        }}
                                    >
                                        <option value="25">25</option>
                                        <option value="50">50</option>
                                        <option value="100">100</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <TechnicianSelectModal
                isOpen={showAssignModal}
                onClose={handleAssignModalClose}
                onSubmit={handleAssignGoogleReviewSubmit}
                title="Assign Google Review to Technician"
            />

            {/* Max Rewards Edit Modal */}
            {showRewardsModal && (
                <div className="modal-overlay" onClick={handleCancelMaxRewards}>
                    <div className="modal-content rewards-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Edit Reward Settings</h3>
                            <button className="modal-close-btn" onClick={handleCancelMaxRewards}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="form-group">
                                <label htmlFor="maxPerformers">Maximum number of top performers:</label>
                                <div className="button-group">
                                    {[1, 2, 3, 4, 5].map(value => (
                                        <button
                                            key={value}
                                            type="button"
                                            className={`button-group__item ${tempMaxRewardsLimit === value ? 'button-group__item--active' : ''}`}
                                            onClick={() => setTempMaxRewardsLimit(value)}
                                        >
                                            {value}
                                        </button>
                                    ))}
                                </div>
                                <p className="help-text">
                                    Sets how many top performers will be displayed and eligible for rewards.
                                    Currently showing: {Math.min(topTechnicians.length, tempMaxRewardsLimit || 1)} of {topTechnicians.length} performers.
                                </p>
                            </div>
                            
                            <div className="form-group">
                                <label htmlFor="maxRewardAmount">Maximum reward amount per performer (USD):</label>
                                <div className="input-with-prefix">
                                    <span className="input-prefix">$</span>
                                    <input
                                        type="number"
                                        id="maxRewardAmount"
                                        min="0"
                                        max="10000"
                                        step="50"
                                        value={tempMaxRewardAmount}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === '') {
                                                setTempMaxRewardAmount('');
                                            } else {
                                                setTempMaxRewardAmount(parseInt(value) || 0);
                                            }
                                        }}
                                        onBlur={(e) => {
                                            if (e.target.value === '' || parseInt(e.target.value) < 0) {
                                                setTempMaxRewardAmount(0);
                                            }
                                        }}
                                        className="form-control with-prefix"
                                    />
                                </div>
                                <p className="help-text">
                                    Any reward amount above this limit will be capped.
                                    {topTechnicians.filter(t => t.totalRewards > (tempMaxRewardAmount || 0)).length > 0 && (
                                        <span className="warning-text">
                                            <br />⚠️ {topTechnicians.filter(t => t.totalRewards > (tempMaxRewardAmount || 0)).length} performer(s) currently exceed this limit.
                                        </span>
                                    )}
                                </p>
                            </div>
                            
                            <div className="form-group">
                                <label htmlFor="rewardAmountPerReview">Reward amount per good review (USD):</label>
                                <div className="input-with-prefix">
                                    <span className="input-prefix">$</span>
                                    <input
                                        type="number"
                                        id="rewardAmountPerReview"
                                        min="0.10"
                                        max="100"
                                        step="0.50"
                                        value={tempRewardAmountPerReview}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === '') {
                                                setTempRewardAmountPerReview('');
                                            } else {
                                                setTempRewardAmountPerReview(parseFloat(value) || 0);
                                            }
                                        }}
                                        onBlur={(e) => {
                                            if (e.target.value === '' || parseFloat(e.target.value) < 0.10) {
                                                setTempRewardAmountPerReview(0.10);
                                            }
                                        }}
                                        className="form-control with-prefix"
                                    />
                                </div>
                                <p className="help-text">
                                    Amount technicians earn for each positive review (4-5 stars). This directly affects their reward calculations.
                                </p>
                            </div>
                            
                            <div className="form-group">
                                <label htmlFor="minimumPayoutThreshold">Minimum payout threshold (USD):</label>
                                <div className="input-with-prefix">
                                    <span className="input-prefix">$</span>
                                    <input
                                        type="number"
                                        id="minimumPayoutThreshold"
                                        min="1"
                                        max="1000"
                                        step="5"
                                        value={tempMinimumPayoutThreshold}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === '') {
                                                setTempMinimumPayoutThreshold('');
                                            } else {
                                                setTempMinimumPayoutThreshold(parseFloat(value) || 0);
                                            }
                                        }}
                                        onBlur={(e) => {
                                            if (e.target.value === '' || parseFloat(e.target.value) < 1) {
                                                setTempMinimumPayoutThreshold(1);
                                            }
                                        }}
                                        className="form-control with-prefix"
                                    />
                                </div>
                                <p className="help-text">
                                    Minimum amount technicians must earn before they can request a payout. Helps reduce processing costs for small amounts.
                                </p>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="button button--secondary"
                                onClick={handleCancelMaxRewards}
                            >
                                Cancel
                            </button>
                            <button
                                className="button button--primary"
                                onClick={handleSaveMaxRewards}
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Reassignment Confirmation Modal */}
            {showReassignConfirmModal && (
                <div className="modal-overlay" onClick={handleReassignCancel}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Confirm Reassignment</h3>
                            <button className="modal-close-btn" onClick={handleReassignCancel}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>
                                Are you sure you want to reassign this review from{' '}
                                <strong>{reviewToReassign?.technicianName}</strong> to a different technician?
                            </p>
                            <p className="text-muted">
                                This action will change the assignment and may affect the technician's performance metrics.
                            </p>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="button button--secondary" 
                                onClick={handleReassignCancel}
                            >
                                Cancel
                            </button>
                            <button 
                                className="button button--primary" 
                                onClick={handleReassignConfirm}
                            >
                                Yes, Reassign
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Persona Edit Modal */}
            {editingPersona === 'profile' && (
                <div className="persona-modal-overlay">
                    <div className="persona-modal">
                        <div className="persona-edit-form">
                            <div className="persona-edit-form__header">
                                <h4>Edit Your Persona</h4>
                                <button
                                    className="persona-edit-form__close"
                                    onClick={() => setEditingPersona(null)}
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            <div className="persona-edit-form__section">
                                <h5>Traits (up to 3)</h5>
                                {personaForm.traits.map((trait, index) => (
                                    <input
                                        key={index}
                                        type="text"
                                        value={trait}
                                        onChange={(e) => handlePersonaInputChange('traits', index, e.target.value)}
                                        placeholder={`Trait ${index + 1}`}
                                        className="persona-edit-form__input"
                                    />
                                ))}
                            </div>

                            <div className="persona-edit-form__section">
                                <h5>Personality (up to 2)</h5>
                                {personaForm.personality.map((personality, index) => (
                                    <input
                                        key={index}
                                        type="text"
                                        value={personality}
                                        onChange={(e) => handlePersonaInputChange('personality', index, e.target.value)}
                                        placeholder={`Personality ${index + 1}`}
                                        className="persona-edit-form__input"
                                    />
                                ))}
                            </div>

                            <div className="persona-edit-form__section">
                                <h5>Communication Style (up to 2)</h5>
                                {personaForm.communicationStyle.map((style, index) => (
                                    <input
                                        key={index}
                                        type="text"
                                        value={style}
                                        onChange={(e) => handlePersonaInputChange('communicationStyle', index, e.target.value)}
                                        placeholder={`Communication Style ${index + 1}`}
                                        className="persona-edit-form__input"
                                    />
                                ))}
                            </div>

                            <div className="persona-edit-form__actions">
                                <button
                                    className="persona-edit-form__btn persona-edit-form__btn--save"
                                    onClick={() => handleSavePersona(technician.crmCode)}
                                >
                                    Save Persona
                                </button>
                                <button
                                    className="persona-edit-form__btn persona-edit-form__btn--cancel"
                                    onClick={() => setEditingPersona(null)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;
